import {
  IInvoicingConfig,
  InvoiceSettingKeys
} from "@wingspanhq/payments/dist/interfaces";

/**
 * The invoicing config the payee's invoice form runs on while the per-client
 * config is unavailable.
 *
 * The v1 read (`GET /payments/payer/:payerId/invoicing-config`) is retired and
 * has no v3 twin, so a payer's per-client invoice rules cannot be resolved. The
 * form falls back to the most permissive shape: every optional field is shown to
 * everyone and nothing is forced, rather than hiding fields a payer may have
 * wanted or requiring ones they never asked for.
 *
 * Deliberate departures from `getDefaultInvoicingConfig` (the test fixture):
 * - `additionalGuidelines` off — the fixture carries sample copy, and there is
 *   no real per-client guideline text to show.
 * - `allowRecurringInvoices` off — recurring/scheduled invoices submit through
 *   the retired v1 invoice-template API, so the option would be a dead end.
 *
 * Restore the real per-client read here once a v3 invoicing-config exists.
 */
export const getFallbackInvoicingConfig = (): IInvoicingConfig => ({
  ...getDefaultInvoicingConfig(),
  [InvoiceSettingKeys.additionalGuidelines]: {
    enabled: false,
    value: ""
  },
  [InvoiceSettingKeys.allowRecurringInvoices]: {
    enabled: false
  }
});

export const getDefaultInvoicingConfig = (): IInvoicingConfig => {
  return {
    [InvoiceSettingKeys.allowOnlySelectedCurrencies]: {
      //TODO: check if this is needed
      enabled: false,
      value: []
    },
    [InvoiceSettingKeys.allowPayeeInitiatedInvoices]: {
      enabled: true
    },
    [InvoiceSettingKeys.additionalGuidelines]: {
      enabled: true,
      value:
        "Client pays it's contractors on a monthly basis. Please submit your invoice by the 5th of the month to ensure timely payment."
    },
    [InvoiceSettingKeys.allowOnlyPredefinedLineItems]: {
      enabled: false,
      value: []
    },
    [InvoiceSettingKeys.allowLineItemDiscounts]: {
      enabled: true
    },
    [InvoiceSettingKeys.allowLineItemReimbursableExpenses]: {
      enabled: true
    },
    [InvoiceSettingKeys.allowLateFees]: {
      enabled: true
    },
    [InvoiceSettingKeys.allowProcessingFeePercentage]: {
      enabled: true
    },
    [InvoiceSettingKeys.allowRecurringInvoices]: {
      enabled: true
    },
    [InvoiceSettingKeys.dueInDays]: {
      enabled: false,
      value: 0
    },
    [InvoiceSettingKeys.allowCollaboratorSplits]: {
      enabled: true
    },
    [InvoiceSettingKeys.requireAttachments]: {
      enabled: false
    },
    [InvoiceSettingKeys.requireProjectName]: {
      enabled: false
    },
    [InvoiceSettingKeys.requirePONumber]: {
      enabled: false
    },
    [InvoiceSettingKeys.allowPaymentReminders]: {
      enabled: true
    }
  };
};
